// STEP-level patcher for the IFCEdit module.
//
// We don't go through web-ifc's WriteLine for attribute mutations:
// version 0.0.77 has a documented bug where round-tripping
// GetLine→WriteLine on existing entities throws "Cannot read
// properties of undefined" depending on the entity type. Instead
// we keep the user's edits in a session overlay (Zustand) and, at
// export time, run a string-level rewrite against the original IFC
// bytes that the viewer already holds in `rawIfc` cache.
//
// The format is ISO 10303-21. Each entity sits on a single logical
// line of the form
//
//     #<expressID>=<TYPE>(arg0,arg1,arg2,...);
//
// args use single quotes for strings (with `''` to escape an inner
// quote), `$` for missing optional, `*` for derived, `.X.` for an
// enum value, and may be nested entities or aggregates.
//
// Supported attributes
// --------------------
// String slots (encoded as `'…'` or `$`):
//
//   Name, Description                → arg 2, 3 (every IfcRoot)
//   ObjectType                       → arg 4 (every IfcObject)
//   Tag                              → arg 7 (every IfcElement)
//   LongName                         → arg 7 (every IfcSpatialElement
//                                     / IfcSpatialStructureElement)
//
// Enum slot (encoded as `.X.` or `$`):
//
//   PredefinedType                   → class- and schema-specific
//                                     position; see predefinedType.ts
//
// We look up which bucket the entity belongs to by walking the
// inheritance chain (string slots) or the META table
// (PredefinedType). As a self-correcting safety net the patcher
// then verifies that the existing argument at the chosen index has
// the right SHAPE before rewriting: a string slot must be `'…'`
// or `$`, an enum slot must be `.X.` or `$`. Wrong shape => refuse.

import { getInheritance } from '@viewer/lib/ifcInheritance'
import { bytesToLatin1, latin1ToBytes } from '@viewer/lib/stepText'
import { argTexto } from '@viewer/lib/step/textoStep'
import { isClassChangeable } from './classChange'
import { getPredefinedTypeInfo } from './predefinedType'
import type { IfcEnumSchema } from './ifcEnums.generated'

export type AttributeEditKind = 'string' | 'enum'

const FIXED_INDEX: Record<string, number> = {
  Name: 2,
  Description: 3,
}

// Attributes whose position is stable across a known inherited bucket.
// `requiredAncestors` is OR-of: as soon as one of the listed classes
// appears in the entity's inheritance chain, the attribute is
// addressable at `index`. The first matching row wins.
const CHAIN_INDEX: Array<{
  attribute: string
  index: number
  requiredAncestors: readonly string[]
}> = [
  { attribute: 'ObjectType', index: 4, requiredAncestors: ['IfcObject'] },
  { attribute: 'Tag', index: 7, requiredAncestors: ['IfcElement'] },
  {
    attribute: 'LongName',
    index: 7,
    // IFC4 introduces IfcSpatialElement between IfcProduct and the
    // structure elements; IFC2X3 stops at IfcSpatialStructureElement.
    // Either of them anchors LongName at position 7.
    requiredAncestors: ['IfcSpatialElement', 'IfcSpatialStructureElement'],
  },
]

// Per-class attribute index overrides. Takes precedence over CHAIN_INDEX
// when present. Used for classes whose inherited attribute count
// shifts the attribute away from the chain-default position; most
// notably IfcSystem subclasses, which carry LongName at varying
// positions (and in differing orders relative to PredefinedType):
//
//   IfcDistributionSystem: ObjectType(4), LongName(5), PredefinedType(6)
//   IfcBuildingSystem:     ObjectType(4), PredefinedType(5), LongName(6)
//   IfcZone (IFC4+):       ObjectType(4), LongName(5)
//
// The safety check still verifies the slot's shape before writing,
// so an override that doesn't match the actual schema (e.g. IFC2X3
// IfcZone has no LongName) is refused gracefully.
const PER_CLASS_INDEX: Record<string, Record<string, number>> = {
  IfcDistributionSystem: { LongName: 5 },
  IfcBuildingSystem: { LongName: 6 },
  IfcZone: { LongName: 5 },
}

const PER_CLASS_INDEX_BY_LOWER: Record<string, string> = Object.keys(
  PER_CLASS_INDEX,
).reduce(
  (acc, k) => {
    acc[k.toLowerCase()] = k
    return acc
  },
  {} as Record<string, string>,
)

export const PERSISTABLE_ATTRIBUTES: readonly string[] = [
  ...Object.keys(FIXED_INDEX),
  ...new Set(CHAIN_INDEX.map((e) => e.attribute)),
  'PredefinedType',
]

/** What shape of STEP arg does an attribute occupy? Drives both the
 *  safety check on the existing value and the encoder of the new
 *  value. */
export function attributeKind(attribute: string): AttributeEditKind {
  return attribute === 'PredefinedType' ? 'enum' : 'string'
}

/** Returns the STEP-arg index for `attribute` on an entity whose IFC
 *  class is `className` (the uppercase name found in the STEP line,
 *  e.g. "IFCWALLSTANDARDCASE"). Returns null when the attribute does
 *  not apply to that class on the supplied schema (schema only
 *  matters for PredefinedType; string attributes are schema-stable). */
export function attributeIndexForClass(
  className: string,
  attribute: string,
  schema: IfcEnumSchema | null = null,
): number | null {
  const fixed = FIXED_INDEX[attribute]
  if (fixed !== undefined) return fixed
  if (attribute === 'PredefinedType') {
    const info = getPredefinedTypeInfo(className, schema)
    return info?.index ?? null
  }
  // Per-class override takes precedence over chain-based lookup:
  // classes like IfcDistributionSystem carry LongName at a different
  // position than the IfcSpatialElement default.
  const canonical = PER_CLASS_INDEX_BY_LOWER[className.toLowerCase()]
  if (canonical) {
    const override = PER_CLASS_INDEX[canonical][attribute]
    if (override !== undefined) return override
  }
  const chain = getInheritance(className)
  if (chain.length === 0) return null
  for (const row of CHAIN_INDEX) {
    if (row.attribute !== attribute) continue
    if (row.requiredAncestors.some((anc) => chain.includes(anc))) {
      return row.index
    }
  }
  return null
}

/** Returns true when `attribute` is editable on entities of class
 *  `className` (and `schema`, for PredefinedType). Used by the
 *  PropertiesPanel to decide which rows should expose the inline
 *  editor. */
export function isAttributeEditableOnClass(
  className: string,
  attribute: string,
  schema: IfcEnumSchema | null = null,
): boolean {
  return attributeIndexForClass(className, attribute, schema) !== null
}

export interface AttributeEdit {
  guid: string
  attribute: string
  /** null clears the attribute (writes IFC NULL = `$`). */
  value: string | null
  /** Schema of the IFC the edit will be applied to. Only consulted
   *  for enum-shaped attributes (PredefinedType) whose position can
   *  vary across schemas. Defaults to null which is fine for
   *  string-slot attributes. */
  schema?: IfcEnumSchema | null
}

/**
 * Apply a list of attribute edits to a STEP/IFC byte array. Returns
 * a fresh Uint8Array; the input is not mutated.
 *
 * Encoding: we operate on the bytes via Latin-1 (one byte per
 * codepoint). That keeps offsets stable even when the IFC contains
 * non-ASCII names. New values are written ASCII-only (non-ASCII as
 * \X2\ directives, see `encodeStepValue`), so the Latin-1 round trip
 * never truncates what the user typed.
 */
export function applyAttributeEdits(
  bytes: Uint8Array,
  edits: AttributeEdit[],
): { bytes: Uint8Array; applied: number; unmatched: string[] } {
  if (edits.length === 0) {
    return { bytes, applied: 0, unmatched: [] }
  }
  let text = bytesToLatin1(bytes)
  let applied = 0
  const unmatched: string[] = []

  for (const edit of edits) {
    const result = patchOneEntity(
      text,
      edit.guid,
      edit.attribute,
      edit.value,
      edit.schema ?? null,
    )
    if (result === null) {
      unmatched.push(`${edit.guid}: ${edit.attribute}`)
      continue
    }
    text = result
    applied += 1
  }
  return {
    bytes: latin1ToBytes(text),
    applied,
    unmatched,
  }
}

export interface ClassEdit {
  guid: string
  /** Target IFC class name in canonical camel-case (e.g. "IfcSlab").
   *  The patcher uppercases it for the STEP line header. */
  newClass: string
}

/**
 * Apply a list of class-change edits to a STEP/IFC byte array. Each
 * edit rewrites the entity header from `#N=IFCXXX(` to
 * `#N=IFCYYY(`, leaving the argument list intact. Returns a fresh
 * Uint8Array; the input is not mutated.
 *
 * Both the source and target classes must be in the compatible-class
 * set (see classChange.ts), which guarantees identical attribute
 * layouts so the argument positions remain valid after the swap.
 * Anything outside that set is refused and reported as unmatched.
 *
 * IMPORTANT: callers should apply class edits BEFORE attribute edits-
 * `applyClassEdits` followed by `applyAttributeEdits`. If the order
 * is inverted, attribute edits that depend on the NEW class (e.g.
 * PredefinedType auto-fallback to NOTDEFINED) might target the wrong
 * schema position.
 */
export function applyClassEdits(
  bytes: Uint8Array,
  edits: ClassEdit[],
): { bytes: Uint8Array; applied: number; unmatched: string[] } {
  if (edits.length === 0) {
    return { bytes, applied: 0, unmatched: [] }
  }
  let text = bytesToLatin1(bytes)
  let applied = 0
  const unmatched: string[] = []

  for (const edit of edits) {
    if (!isClassChangeable(edit.newClass)) {
      unmatched.push(`${edit.guid}: → ${edit.newClass} (não compatível)`)
      continue
    }
    const result = patchOneClass(text, edit.guid, edit.newClass)
    if (result === null) {
      unmatched.push(`${edit.guid}: → ${edit.newClass}`)
      continue
    }
    text = result
    applied += 1
  }
  return {
    bytes: latin1ToBytes(text),
    applied,
    unmatched,
  }
}

/** Locate the entity line for `guid` and rewrite arg at the class-
 *  resolved index. Returns the new full text or null when the entity
 *  wasn't found, the attribute does not apply to the entity's class,
 *  or the safety check rejected the rewrite. */
function patchOneEntity(
  text: string,
  guid: string,
  attribute: string,
  newValue: string | null,
  schema: IfcEnumSchema | null,
): string | null {
  // Find a quoted occurrence of the guid. IFC GlobalIds are 22-char
  // base64-ish strings, so collisions outside argument 0 are
  // exceedingly rare. We additionally verify the `'guid'` lies in
  // the FIRST quoted slot of an entity assignment.
  const needle = `'${guid}'`
  let from = 0
  while (true) {
    const hit = text.indexOf(needle, from)
    if (hit === -1) return null
    // Walk back to the start of this entity line: `#<num>=<TYPE>(`.
    const lineStart = locateLineStart(text, hit)
    if (lineStart === -1) {
      from = hit + needle.length
      continue
    }
    const openParen = text.indexOf('(', lineStart)
    if (openParen === -1 || openParen > hit) {
      from = hit + needle.length
      continue
    }
    // The first non-whitespace after `(` should be the start of arg 0.
    // `'guid'` must be that first arg; otherwise this hit is just a
    // string elsewhere in the file (rare but possible).
    let firstArgStart = openParen + 1
    while (firstArgStart < text.length && /\s/.test(text[firstArgStart])) {
      firstArgStart += 1
    }
    if (firstArgStart !== hit) {
      from = hit + needle.length
      continue
    }
    // Found a real entity assignment. Extract the IFC class name from
    // the text between `=` and `(` so we can resolve subclass-specific
    // attribute positions.
    const className = extractClassName(text, lineStart, openParen)
    if (!className) return null
    const attrIndex = attributeIndexForClass(className, attribute, schema)
    if (attrIndex === null) return null
    const closeParen = matchCloseParen(text, openParen)
    if (closeParen === -1) return null
    const argsStr = text.slice(openParen + 1, closeParen)
    const spans = splitTopLevelArgs(argsStr)
    if (attrIndex >= spans.length) return null

    // Safety check: the existing argument must have the shape we
    // expect for this attribute kind. If it doesn't (e.g. an entity
    // ref `#123` where we thought a string lived) we mis-guessed
    // the schema position and refuse to rewrite: never corrupt
    // the file.
    const target = spans[attrIndex]
    const existing = argsStr.slice(target.start, target.end).trim()
    const kind = attributeKind(attribute)
    if (!matchesSlotShape(existing, kind)) return null

    const replacement = encodeStepValue(newValue, kind)
    const newArgsStr =
      argsStr.slice(0, target.start) +
      replacement +
      argsStr.slice(target.end)
    return (
      text.slice(0, openParen + 1) +
      newArgsStr +
      text.slice(closeParen)
    )
  }
}

/** Locate the entity line for `guid` and rewrite its class header
 *  (`#N=IFCXXX(` → `#N=IFCYYY(`). Returns the new full text or null
 *  when the entity wasn't found, when the source class isn't in the
 *  compatible-class set, or when the line doesn't match the expected
 *  shape. */
function patchOneClass(
  text: string,
  guid: string,
  newCanonicalClass: string,
): string | null {
  const needle = `'${guid}'`
  let from = 0
  while (true) {
    const hit = text.indexOf(needle, from)
    if (hit === -1) return null
    const lineStart = locateLineStart(text, hit)
    if (lineStart === -1) {
      from = hit + needle.length
      continue
    }
    const openParen = text.indexOf('(', lineStart)
    if (openParen === -1 || openParen > hit) {
      from = hit + needle.length
      continue
    }
    let firstArgStart = openParen + 1
    while (firstArgStart < text.length && /\s/.test(text[firstArgStart])) {
      firstArgStart += 1
    }
    if (firstArgStart !== hit) {
      from = hit + needle.length
      continue
    }
    const oldClass = extractClassName(text, lineStart, openParen)
    if (!oldClass) return null
    // Refuse the rewrite if the SOURCE class isn't in the
    // compatible-class set: we only know the layouts are identical
    // for the curated list. Anything outside is too risky.
    if (!isClassChangeable(oldClass)) return null
    const eq = text.indexOf('=', lineStart)
    if (eq === -1 || eq >= openParen) return null
    const newHeader = newCanonicalClass.toUpperCase()
    return (
      text.slice(0, eq + 1) +
      newHeader +
      text.slice(openParen)
    )
  }
}

/** Pull the IFC class name out of `#<num>=<CLASS>(`. Skips the `#N=`
 *  prefix and returns the bare class name, or null when the line
 *  doesn't match the expected shape. */
function extractClassName(
  text: string,
  lineStart: number,
  openParen: number,
): string | null {
  const eq = text.indexOf('=', lineStart)
  if (eq === -1 || eq >= openParen) return null
  return text.slice(eq + 1, openParen).trim() || null
}

/** Does the existing STEP arg match the expected slot shape for this
 *  edit kind? The check is intentionally strict so the patcher
 *  refuses any other shape (number, entity ref, aggregate, typed
 *  wrapper for unsupported types). */
function matchesSlotShape(arg: string, kind: AttributeEditKind): boolean {
  if (arg === '$' || arg === '*') return true
  if (kind === 'string') {
    return arg.startsWith("'") && arg.endsWith("'") && arg.length >= 2
  }
  if (kind === 'enum') {
    // STEP enum literal: `.NAME.` with uppercase letters / digits /
    // underscore between the dots. Empty enum (`.`) is not legal.
    return /^\.[A-Z_][A-Z0-9_]*\.$/.test(arg)
  }
  return false
}

/** Encode a user-typed value as a STEP literal of the right kind. */
function encodeStepValue(
  value: string | null,
  kind: AttributeEditKind,
): string {
  if (value === null || value === '') return '$'
  if (kind === 'string') {
    // ISO 10303-21 only allows characters 32-126 inside the quotes:
    // anything else goes as \X2\...\X0\ directives, and quotes and
    // backslashes are doubled. Writing "ç" as a raw byte (or "€" cut to
    // one byte) corrupted the text, and a lone backslash made the file
    // unreadable for IfcOpenShell.
    return argTexto(value)
  }
  // Enum: uppercase identifier between dots.
  return `.${value.toUpperCase()}.`
}

function locateLineStart(text: string, pos: number): number {
  // Each entity line begins after the previous `;` (or BOF). Walk
  // back until we hit `;` then verify a `#` follows it.
  for (let i = pos; i >= 0; i--) {
    if (i === 0) return text[0] === '#' ? 0 : -1
    if (text[i] === ';') {
      let j = i + 1
      while (j < text.length && /\s/.test(text[j])) j += 1
      return text[j] === '#' ? j : -1
    }
  }
  return -1
}

function matchCloseParen(text: string, openIdx: number): number {
  let depth = 1
  let inQuote = false
  for (let i = openIdx + 1; i < text.length; i++) {
    const ch = text[i]
    if (inQuote) {
      if (ch === "'") {
        // `''` is an escaped quote inside a STEP string, skip pair.
        if (text[i + 1] === "'") {
          i += 1
          continue
        }
        inQuote = false
      }
      continue
    }
    if (ch === "'") {
      inQuote = true
      continue
    }
    if (ch === '(') depth += 1
    else if (ch === ')') {
      depth -= 1
      if (depth === 0) return i
    }
  }
  return -1
}

interface Span {
  start: number
  end: number
}

function splitTopLevelArgs(s: string): Span[] {
  const out: Span[] = []
  let argStart = 0
  let depth = 0
  let inQuote = false
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (inQuote) {
      if (ch === "'") {
        if (s[i + 1] === "'") {
          i += 1
          continue
        }
        inQuote = false
      }
      continue
    }
    if (ch === "'") {
      inQuote = true
      continue
    }
    if (ch === '(') depth += 1
    else if (ch === ')') depth -= 1
    else if (ch === ',' && depth === 0) {
      out.push({ start: argStart, end: i })
      argStart = i + 1
    }
  }
  out.push({ start: argStart, end: s.length })
  return out
}
